import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/App";
import {
  Users,
  Search,
  Edit,
  Trash2,
  Plus,
  Shield,
  Building2,
  Mail,
  UserCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const roles = [
  { value: "super_admin", label: "Super Administrador" },
  { value: "admin_conferencia", label: "Admin Conferencia Episcopal" },
  { value: "admin_diocesano", label: "Admin Diocesano" },
  { value: "admin_vicaria", label: "Admin Vicaría" },
  { value: "parroco", label: "Párroco" },
  { value: "vicario_parroquial", label: "Vicario Parroquial" },
  { value: "secretaria", label: "Secretaria" },
  { value: "lector", label: "Solo Lectura" },
];

const roleColors = {
  super_admin: "bg-purple-100 text-purple-800 border-purple-200",
  admin_conferencia: "bg-blue-100 text-blue-800 border-blue-200",
  admin_diocesano: "bg-indigo-100 text-indigo-800 border-indigo-200",
  admin_vicaria: "bg-cyan-100 text-cyan-800 border-cyan-200",
  parroco: "bg-emerald-100 text-emerald-800 border-emerald-200",
  vicario_parroquial: "bg-teal-100 text-teal-800 border-teal-200",
  secretaria: "bg-amber-100 text-amber-800 border-amber-200",
  lector: "bg-stone-100 text-stone-800 border-stone-200",
};

export default function UsuariosPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [entidades, setEntidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    email: "",
    password: "",
    rol: "secretaria",
    entidad_id: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, entidadesRes] = await Promise.all([
        api.get("/users"),
        api.get("/entidades"),
      ]);
      setUsers(usersRes.data);
      setEntidades(entidadesRes.data);
    } catch (error) {
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        nombre: user.nombre || "",
        apellido: user.apellido || "",
        email: user.email || "",
        password: "",
        rol: user.rol || "secretaria",
        entidad_id: user.entidad_id || "",
      });
    } else {
      setEditingUser(null);
      setFormData({
        nombre: "",
        apellido: "",
        email: "",
        password: "",
        rol: "secretaria",
        entidad_id: "",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingUser) {
        // Update user
        const updateData = {
          nombre: formData.nombre,
          apellido: formData.apellido,
          rol: formData.rol,
          entidad_id: formData.entidad_id || null,
        };
        await api.put(`/users/${editingUser.id}`, updateData);
        toast.success("Usuario actualizado correctamente");
      } else {
        // Create new user
        if (!formData.password) {
          toast.error("La contraseña es requerida");
          return;
        }
        await api.post("/auth/register", formData);
        toast.success("Usuario creado correctamente");
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar el usuario");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/users/${deleteId}`);
      toast.success("Usuario eliminado correctamente");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar el usuario");
    } finally {
      setDeleteId(null);
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.nombre?.toLowerCase().includes(search.toLowerCase()) ||
      u.apellido?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const getRoleLabel = (rol) => {
    const found = roles.find((r) => r.value === rol);
    return found ? found.label : rol;
  };

  const getEntidadName = (entidadId) => {
    const found = entidades.find((e) => e.id === entidadId);
    return found ? found.nombre : "—";
  };

  // Check if current user can manage users
  const canManageUsers = ["super_admin", "admin_conferencia", "admin_diocesano"].includes(
    currentUser?.rol
  );

  if (!canManageUsers) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="border-stone-200 bg-white p-8 text-center">
          <Shield className="w-12 h-12 mx-auto text-stone-300 mb-4" />
          <h2 className="text-xl font-serif text-stone-800 mb-2">Acceso Restringido</h2>
          <p className="text-stone-600">
            No tienes permisos para gestionar usuarios. Contacta al administrador del sistema.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="usuarios-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif text-rose-950 tracking-tight flex items-center gap-3">
            <Users className="w-8 h-8 text-indigo-600" strokeWidth={1.5} />
            Gestión de Usuarios
          </h1>
          <p className="text-stone-600 mt-1">Administra los usuarios del sistema</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-rose-950 hover:bg-rose-900 text-white"
          data-testid="nuevo-usuario-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Usuario
        </Button>
      </div>

      {/* Search */}
      <Card className="border-stone-200 bg-white">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              placeholder="Buscar por nombre, apellido o email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-11 bg-white border-stone-200"
              data-testid="search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="border-stone-200 bg-white">
        <CardHeader>
          <CardTitle className="text-lg font-medium text-stone-800">
            {filteredUsers.length} usuarios
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-stone-400 animate-pulse">
              Cargando...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-8 text-center text-stone-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No se encontraron usuarios</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="table-ledger">
                <TableHeader>
                  <TableRow>
                    <TableHead className="px-6 py-4">Usuario</TableHead>
                    <TableHead className="px-6 py-4">Email</TableHead>
                    <TableHead className="px-6 py-4">Rol</TableHead>
                    <TableHead className="px-6 py-4">Entidad</TableHead>
                    <TableHead className="px-6 py-4 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-rose-100 flex items-center justify-center text-rose-700 font-medium text-sm">
                            {user.nombre?.charAt(0)}
                            {user.apellido?.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium text-stone-800">
                              {user.nombre} {user.apellido}
                            </p>
                            {user.id === currentUser?.id && (
                              <Badge variant="outline" className="text-xs mt-1">
                                <UserCheck className="w-3 h-3 mr-1" />
                                Tú
                              </Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        <div className="flex items-center gap-2 text-stone-600">
                          <Mail className="w-4 h-4" />
                          {user.email}
                        </div>
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        <Badge
                          variant="outline"
                          className={roleColors[user.rol] || roleColors.lector}
                        >
                          {getRoleLabel(user.rol)}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        {user.entidad_id ? (
                          <div className="flex items-center gap-2 text-stone-600">
                            <Building2 className="w-4 h-4" />
                            {getEntidadName(user.entidad_id)}
                          </div>
                        ) : (
                          <span className="text-stone-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleOpenDialog(user)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {user.id !== currentUser?.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setDeleteId(user.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {editingUser ? "Editar Usuario" : "Nuevo Usuario"}
            </DialogTitle>
            <DialogDescription>
              {editingUser
                ? "Modifica los datos del usuario"
                : "Crea una nueva cuenta de usuario"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre *</Label>
                <Input
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  className="h-10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="apellido">Apellido *</Label>
                <Input
                  id="apellido"
                  name="apellido"
                  value={formData.apellido}
                  onChange={handleChange}
                  required
                  className="h-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Correo Electrónico *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={!!editingUser}
                className="h-10"
              />
            </div>

            {!editingUser && (
              <div className="space-y-2">
                <Label htmlFor="password">Contraseña *</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required={!editingUser}
                  className="h-10"
                  placeholder="Mínimo 6 caracteres"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="rol">Rol *</Label>
              <Select
                value={formData.rol}
                onValueChange={(value) => setFormData({ ...formData, rol: value })}
              >
                <SelectTrigger className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {roles.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="entidad_id">Entidad Asignada</Label>
              <Select
                value={formData.entidad_id || "none"}
                onValueChange={(value) =>
                  setFormData({ ...formData, entidad_id: value === "none" ? "" : value })
                }
              >
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Sin asignar" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-[200px]">
                  <SelectItem value="none">Sin asignar</SelectItem>
                  {entidades.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-rose-950 hover:bg-rose-900 text-white">
                {editingUser ? "Actualizar" : "Crear Usuario"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este usuario?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El usuario perderá acceso al sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
